import React, {Dispatch, SetStateAction, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {WizardContext, WIZARD_CONFIG, WizardStepKey, getStepByKey} from './steps';
import {
    buildAdminRequestParams,
    createPlayPresentationUrl,
    createShortPresentationUrl,
    openPlayerWindow,
} from '../../../utils/presentation';
import {copyToClipboard} from '../../../utils/copyToClipboard';
import Button from '../../../incase-domain/components/button/Button';
import {IPresentationMeta} from '../../../incase-domain/presentation';
import {useCreateAutoSlug} from '../../../service/hooks/useSlug';

interface IWizardFooterProps {
    wizardContext: WizardContext;
    setWizardContext: Dispatch<SetStateAction<WizardContext>>;
    presentation: IPresentationMeta;
    disabledNext?: boolean;
    selectedRunOption: string | null;
    close: () => void;
}

function WizardFooter({
    wizardContext,
    setWizardContext,
    presentation,
    disabledNext,
    selectedRunOption,
    close,
}: IWizardFooterProps): JSX.Element {
    const {t} = useTranslation();
    const {mutateAsync: createAutoSlug} = useCreateAutoSlug();
    const getPreviousStepKey = useCallback(
        (currentStepKey: string): string | null => {
            const stepsReturnToRunCase = [WIZARD_CONFIG.PREVIEW.key, WIZARD_CONFIG.ON_DEMAND.key, WIZARD_CONFIG.LIVE_EVENT.key];

            if (stepsReturnToRunCase.includes(currentStepKey)) {
                return WIZARD_CONFIG.RUN_CASE.key;
            }

            if (currentStepKey === WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS.key) {
                return wizardContext.previousStep?.key || WIZARD_CONFIG.ON_DEMAND.key;
            }

            if (currentStepKey === WIZARD_CONFIG.LIVE_EVENT.options.ROOM_SELECTION.key) {
                return WIZARD_CONFIG.LIVE_EVENT.key;
            }

            if (currentStepKey === WIZARD_CONFIG.LIVE_EVENT.options.ROOM_CREATION.key) {
                return WIZARD_CONFIG.LIVE_EVENT.key;
            }

            const steps = Object.keys(WIZARD_CONFIG) as WizardStepKey[];
            const currentIndex = steps.indexOf(currentStepKey as WizardStepKey);
            return currentIndex > 0 ? steps[currentIndex - 1] : null;
        },
        [wizardContext.previousStep?.key]
    );

    const handleFooterBackButton = useCallback(() => {
        const currentStepKey = wizardContext.currentStep.key;
        const previousStepKey = getPreviousStepKey(currentStepKey);

        if (previousStepKey) {
            setWizardContext((prev) => ({
                ...prev,
                currentStep: getStepByKey(previousStepKey),
            }));
        }
    }, [wizardContext.currentStep.key, getPreviousStepKey, setWizardContext]);

    const handleFooterNextButton = useCallback(() => {
        const {currentStep} = wizardContext;

        const handleCopyLinkToClipboard = async (): Promise<void> => {
            try {
                const slugDoc = await createAutoSlug({presentationId: presentation._id});
                await copyToClipboard(createShortPresentationUrl(slugDoc.slug));
                toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
            } catch (err) {
                toast.info(t('presEditPage.subNavBar.failedToCopy'));
            }
        };

        if (currentStep.key === WIZARD_CONFIG.RUN_CASE.key && selectedRunOption) {
            if (selectedRunOption === WIZARD_CONFIG.RUN_CASE.options.PREVIEW) {
                openPlayerWindow(async () => {
                    const slugDoc = await createAutoSlug({
                        presentationId: presentation._id,
                        params: buildAdminRequestParams(presentation),
                    });
                    return createShortPresentationUrl(slugDoc.slug);
                }, createPlayPresentationUrl({presentation}));
                return;
            }

            const modeToNextStep: Record<string, (typeof WIZARD_CONFIG)[keyof typeof WIZARD_CONFIG]> = {
                [WIZARD_CONFIG.RUN_CASE.options.ON_DEMAND]: WIZARD_CONFIG.ON_DEMAND,
                [WIZARD_CONFIG.RUN_CASE.options.LIVE_EVENT]: WIZARD_CONFIG.LIVE_EVENT,
            };

            const nextStep = modeToNextStep[selectedRunOption];
            if (nextStep) {
                setWizardContext((prev) => ({
                    ...prev,
                    currentStep: nextStep,
                }));
            }
            return;
        }

        switch (currentStep.key) {
            case WIZARD_CONFIG.RUN_CASE.options.PREVIEW:
                openPlayerWindow(async () => {
                    const slugDoc = await createAutoSlug({
                        presentationId: presentation._id,
                        params: buildAdminRequestParams(presentation),
                    });
                    return createShortPresentationUrl(slugDoc.slug);
                }, createPlayPresentationUrl({presentation}));
                break;
            case WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS.key:
                close();
                break;
            case WIZARD_CONFIG.ON_DEMAND.key:
                if (!presentation.requestParams?.length) {
                    handleCopyLinkToClipboard();
                    break;
                }
                setWizardContext({
                    ...wizardContext,
                    currentStep: WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS,
                    previousStep: currentStep,
                });
                break;
            case WIZARD_CONFIG.LIVE_EVENT.options.ROOM_SELECTION.key:
                if (!disabledNext) {
                    setWizardContext({
                        ...wizardContext,
                        currentStep: WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS,
                        previousStep: currentStep,
                    });
                }
                break;
            default:
                break;
        }
    }, [wizardContext, selectedRunOption, presentation, t, setWizardContext, close, disabledNext, createAutoSlug]);

    const getNextButtonText = useCallback(() => {
        const {currentStep} = wizardContext;

        if (currentStep.key === WIZARD_CONFIG.RUN_CASE.key) {
            if (selectedRunOption === WIZARD_CONFIG.RUN_CASE.options.PREVIEW) {
                return t('common.start');
            }
            return t('common.next');
        }
        if (currentStep.key === WIZARD_CONFIG.RUN_CASE.options.PREVIEW) {
            return t('common.start');
        }
        if (currentStep.key === WIZARD_CONFIG.ON_DEMAND.key && !presentation.requestParams?.length) {
            return t('editorPage.createPresentationLinksModal.copyLink');
        }
        if (currentStep.key === WIZARD_CONFIG.ON_DEMAND.key && !!presentation.requestParams?.length) {
            return t('editorPage.createPresentationLinksModal.createLinks');
        }

        if (currentStep.key === WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS.key) {
            return t('common.finish');
        }

        return t('common.next');
    }, [wizardContext, presentation.requestParams?.length, t, selectedRunOption]);

    const getDisabledNext = useCallback(() => {
        const {currentStep} = wizardContext;

        if (currentStep.key === WIZARD_CONFIG.RUN_CASE.key) {
            return !selectedRunOption;
        }

        return !currentStep || disabledNext;
    }, [disabledNext, wizardContext, selectedRunOption]);

    const getBackButtonText = useCallback(() => {
        return t('navigation.back');
    }, [t]);

    const showNextButton = useMemo(() => {
        const hiddenSteps = [WIZARD_CONFIG.LIVE_EVENT.key];
        return !hiddenSteps.includes(wizardContext.currentStep.key);
    }, [wizardContext.currentStep.key]);

    const showBackButton = useMemo(() => {
        return wizardContext.currentStep.key !== WIZARD_CONFIG.RUN_CASE.key;
    }, [wizardContext.currentStep.key]);

    return (
        <div className="c-case_wizard_footer">
            <div className="c-case_wizard_footer__left">
                {showBackButton && (
                    <Button onClick={handleFooterBackButton} type="button" variant="ghost">
                        {getBackButtonText()}
                    </Button>
                )}
            </div>

            <div className="c-case_wizard_footer__right">
                {showNextButton && (
                    <Button onClick={handleFooterNextButton} type="button" variant="solid" disabled={getDisabledNext()}>
                        {getNextButtonText()}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default WizardFooter;
